import React from 'react';
import { useNavigate } from 'react-router-dom';
import colors, { fonts, shadows } from '../theme';

export default function SecurityPage() {
  const navigate = useNavigate();

  return (
    <div style={{
      fontFamily: fonts.landing,
      background: colors.landing.bg,
      minHeight: '100vh',
      color: colors.landing.text,
    }}>
      <style>{`
        .gradient-text {
          background: ${colors.gradients.coral};
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>
      {/* Header/Nav */}
      <nav style={{
        padding: '24px 48px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${colors.overlay.coralBorder}`,
      }}>
        <div
          onClick={() => navigate('/')}
          style={{
            fontSize: '24px',
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          <span className="gradient-text">miblio</span>
        </div>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          <button onClick={() => navigate('/login')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.text,
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 600,
          }}>
            Sign In
          </button>
          <button onClick={() => navigate('/register')} style={{
            background: colors.gradients.coral,
            border: 'none',
            color: colors.white,
            padding: '12px 32px',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 700,
            boxShadow: shadows.coralMd,
          }}>
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        padding: '80px 48px 60px',
        textAlign: 'center',
        maxWidth: '900px',
        margin: '0 auto',
      }}>
        <div style={{
          display: 'inline-block',
          padding: '8px 20px',
          background: colors.overlay.coralLight,
          borderRadius: '24px',
          marginBottom: '24px',
        }}>
          <span style={{
            fontSize: '14px',
            fontWeight: 700,
            color: colors.landing.coral,
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}>
            🔒 Security & Privacy
          </span>
        </div>

        <h1 style={{
          fontSize: '64px',
          fontWeight: 800,
          lineHeight: 1.1,
          marginBottom: '24px',
          background: colors.gradients.coral,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>
          Your Data, Your Control
        </h1>

        <p style={{
          fontSize: '22px',
          lineHeight: 1.6,
          opacity: 0.9,
          marginBottom: '48px',
        }}>
          We take security seriously. Here's how we protect your team's knowledge.
        </p>
      </section>

      {/* Main Content */}
      <section style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '0 48px 80px',
      }}>
        {/* Key Principles */}
        <div style={{
          background: colors.white,
          padding: '48px',
          borderRadius: '24px',
          marginBottom: '40px',
          boxShadow: shadows.coralMd,
        }}>
          <h2 style={{
            fontSize: '36px',
            fontWeight: 800,
            marginBottom: '32px',
            color: colors.landing.text,
          }}>
            Our Security Principles
          </h2>

          <div style={{ display: 'grid', gap: '32px' }}>
            {[
              {
                icon: '🔐',
                title: 'Your Content Stays Yours',
                description: 'For Notion, Google Docs, and Confluence integrations, we never store your actual documentation. Content is fetched live from your workspace when users view it. We only store metadata and AI-generated quiz questions.',
              },
              {
                icon: '🔒',
                title: 'Encryption Everywhere',
                description: 'All data is encrypted at rest and in transit using industry-standard protocols. API credentials are encrypted with AES-256-GCM before storage.',
              },
              {
                icon: '🏢',
                title: 'Complete Isolation',
                description: 'Your organization\'s data is completely isolated from other customers. Multi-tenant architecture ensures no cross-contamination.',
              },
              {
                icon: '👥',
                title: 'Access Control',
                description: 'Role-based permissions (Admin, Manager, User) ensure team members only see what they should. JWT authentication protects all sessions.',
              },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                gap: '20px',
                alignItems: 'start',
              }}>
                <div style={{
                  fontSize: '40px',
                  minWidth: '60px',
                }}>
                  {item.icon}
                </div>
                <div>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: 700,
                    marginBottom: '8px',
                    color: colors.landing.text,
                  }}>
                    {item.title}
                  </h3>
                  <p style={{
                    fontSize: '16px',
                    lineHeight: 1.6,
                    color: colors.landing.textLight,
                    margin: 0,
                  }}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* What We Store */}
        <div style={{
          background: colors.white,
          padding: '48px',
          borderRadius: '24px',
          marginBottom: '40px',
          boxShadow: shadows.coralMd,
        }}>
          <h2 style={{
            fontSize: '36px',
            fontWeight: 800,
            marginBottom: '32px',
            color: colors.landing.text,
          }}>
            What We Store
          </h2>

          <div style={{ display: 'grid', gap: '24px' }}>
            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 700,
                marginBottom: '12px',
                color: colors.landing.coral,
              }}>
                ✅ We Store:
              </h3>
              <ul style={{
                fontSize: '16px',
                lineHeight: 1.8,
                color: colors.landing.textLight,
                paddingLeft: '24px',
              }}>
                <li>Metadata (titles, descriptions, timestamps)</li>
                <li>AI-generated quiz questions</li>
                <li>User names and emails</li>
                <li>Quiz scores and completion records</li>
                <li>Manually created knowledge entries</li>
              </ul>
            </div>

            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 700,
                marginBottom: '12px',
                color: colors.landing.coral,
              }}>
                ❌ We Don't Store:
              </h3>
              <ul style={{
                fontSize: '16px',
                lineHeight: 1.8,
                color: colors.landing.textLight,
                paddingLeft: '24px',
              }}>
                <li>Your Notion, Google Docs, or Confluence content</li>
                <li>Passwords (only bcrypt hashes with salt)</li>
                <li>Payment information (handled by Stripe)</li>
                <li>Session tokens server-side</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Infrastructure */}
        <div style={{
          background: colors.white,
          padding: '48px',
          borderRadius: '24px',
          marginBottom: '40px',
          boxShadow: shadows.coralMd,
        }}>
          <h2 style={{
            fontSize: '36px',
            fontWeight: 800,
            marginBottom: '32px',
            color: colors.landing.text,
          }}>
            Infrastructure & Compliance
          </h2>

          <div style={{ display: 'grid', gap: '20px' }}>
            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 700,
                marginBottom: '8px',
                color: colors.landing.text,
              }}>
                Trusted Partners
              </h3>
              <p style={{
                fontSize: '16px',
                lineHeight: 1.6,
                color: colors.landing.textLight,
                margin: 0,
              }}>
                We use industry-leading providers: Supabase for databases, Railway for backend hosting, and Vercel for frontend delivery. All located in secure US data centers.
              </p>
            </div>

            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 700,
                marginBottom: '8px',
                color: colors.landing.text,
              }}>
                Enterprise Ready
              </h3>
              <p style={{
                fontSize: '16px',
                lineHeight: 1.6,
                color: colors.landing.textLight,
                margin: 0,
              }}>
                Planning to target enterprise customers? We're building toward SOC 2 compliance and offer dedicated support for security-conscious organizations.
              </p>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div style={{
          background: colors.overlay.coralLight,
          padding: '48px',
          borderRadius: '24px',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: '32px',
            fontWeight: 800,
            marginBottom: '16px',
            color: colors.landing.text,
          }}>
            Have Security Questions?
          </h2>
          <p style={{
            fontSize: '18px',
            lineHeight: 1.6,
            color: colors.landing.textLight,
            marginBottom: '32px',
          }}>
            We're transparent about our security practices. Reach out anytime.
          </p>
          <button
            onClick={() => window.location.href = 'mailto:security@miblio.io'}
            style={{
              background: colors.gradients.coral,
              border: 'none',
              color: colors.white,
              padding: '16px 40px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 700,
              boxShadow: shadows.coralLg,
            }}
          >
            Contact Security Team
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '48px',
        textAlign: 'center',
        background: colors.landing.bg,
        borderTop: `1px solid ${colors.overlay.coralBorder}`,
      }}>
        <div style={{
          fontSize: '24px',
          fontWeight: 800,
          marginBottom: '16px',
        }}>
          <span className="gradient-text">miblio</span>
        </div>
        <div style={{
          display: 'flex',
          gap: '24px',
          justifyContent: 'center',
          marginBottom: '16px',
        }}>
          <button onClick={() => navigate('/')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
          }}>
            Home
          </button>
          <button onClick={() => navigate('/security')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
          }}>
            Security
          </button>
          <button onClick={() => navigate('/privacy')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
          }}>
            Privacy
          </button>
          <button onClick={() => navigate('/terms')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
          }}>
            Terms
          </button>
        </div>
        <p style={{
          fontSize: '14px',
          color: colors.landing.textLight,
        }}>
          © 2024 Miblio. Built for teams who learn together.
        </p>
      </footer>
    </div>
  );
}
